import request from "../../utils/request";
import { Result, Company } from "../../models/general";
import type {
  BillingManagementMode,
  BillingSubscriptionOrigin,
} from "./PlatformBilling";

/* ───────────────────────── Types ───────────────────────── */

export type SubscriptionTier = "Free" | "Trial" | "Paid";
export type SubscriptionStatus = "Active" | "TrialExpired" | "PaymentOverdue";

export interface CompanySubscriptionView {
  id: number;
  name: string;
  imageUrl?: string;
  email?: string;
  tier: SubscriptionTier | number;
  status: SubscriptionStatus | number;
  /** ⚠️ CO-227 — always null now. Retained for wire compatibility; see canStartTrial below. */
  trialStartedAt?: string;
  trialEndsAt?: string;
  trialDaysRemaining?: number;
  paidUntil?: string;
  paidDaysRemaining?: number;
  userCount: number;
  subscriptionUpdatedAt?: string;
  subscriptionNotes?: string;
  referenceSlug?: string | null;
  referenceName?: string | null;
  referenceRaw?: string | null;
  createdOn: string;
}

export interface SignupReferenceView {
  id: number;
  slug: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  companyCount: number;
  signupUrl: string;
  createdOn: string;
}

export interface SignupReferenceInput {
  slug: string;
  name: string;
  description?: string | null;
  isActive: boolean;
}

/**
 * CO-38 — a bulk action's per-company outcome. `failed` is a REPORT, not telemetry: every row must be
 * rendered for the operator (company name + why), never summarised into a count or logged to the
 * console. `code` is the machine-readable refusal (e.g. BILLING_MANAGED_SUBSCRIPTION,
 * REVOKE_CLEAR_NEVER_EXPIRES_FIRST) and is what the UI groups on; `reason` is the server's prose.
 */
export interface BulkOperationFailure {
  companyId: number;
  companyName?: string | null;
  code?: string | null;
  reason: string;
}

export interface BulkOperationResultView {
  succeeded: number[];
  failed: BulkOperationFailure[];
}

export interface BulkMarkPaidInput {
  companyIds: number[];
  paidUntil: string | null;
  notes: string | null;
}

export interface BulkRevokeInput {
  companyIds: number[];
  notes: string;
}

export interface SubscriptionAuditLogView {
  id: number;
  action: string;
  fromTier: SubscriptionTier | number;
  toTier: SubscriptionTier | number;
  fromStatus: SubscriptionStatus | number;
  toStatus: SubscriptionStatus | number;
  trialEndsAtBefore?: string;
  trialEndsAtAfter?: string;
  paidUntilBefore?: string;
  paidUntilAfter?: string;
  notes?: string;
  performedByUserId?: string;
  performedByName?: string;
  performedAt: string;

  /*
   * ── Management-state transition. OPTIONAL, and it may stay absent for a while ──
   *
   * The existing audit writer squashes every override into three legacy `SubscriptionAction` values
   * and writes `before == after` for every field, so it CANNOT yet express "management moved from the
   * customer to us". A `ManagementModeChanged` action plus real before/after columns is a separate
   * migration (design Q3). Until that lands, the history sheet recognises the change from the action
   * string and renders the reason — it must degrade to a labelled entry, never to a blank row.
   */
  fromManagementMode?: BillingManagementMode | number | null;
  toManagementMode?: BillingManagementMode | number | null;
  origin?: BillingSubscriptionOrigin | number | null;
}

export interface CompanySubscriptionDetailView extends CompanySubscriptionView {
  history: SubscriptionAuditLogView[];
}

/**
 * Caller-scoped subscription state used by the global Free/Trial banner.
 * Mirrors `Mowazaf.Server.Data.General.ViewModels.MySubscriptionView`. Backed by
 * GET /api/Subscription/me which is open to any authenticated user.
 */
export interface MySubscriptionView {
  companyId?: number | null;
  tier: SubscriptionTier | number;
  status: SubscriptionStatus | number;
  /** ⚠️ CO-227 — always null now. Retained for wire compatibility; see canStartTrial below. */
  trialStartedAt?: string;
  trialEndsAt?: string;
  trialDaysRemaining?: number;
  paidUntil?: string;
  paidDaysRemaining?: number;
  /**
   * Operator-granted perpetual non-expiry. When true the term end is a date, not a deadline:
   * nothing lapses, so the renewal band must stay silent. Optional because older backends omit it.
   */
  neverExpires?: boolean;
  /** True when the caller has the system-wide Operator role — banner suppresses. */
  isOperator: boolean;
  /**
   * ⚠️ CO-227 — ALWAYS FALSE. Trial is retired; the server pins this to false for every caller.
   * Kept on the wire so the Flutter app and any pre-swap SPA bundle keep deserializing. Never
   * render a "start trial" affordance from it.
   */
  canStartTrial: boolean;
  /** ⚠️ CO-227 — ALWAYS 0. Was the advertised trial length; there is no trial to advertise. */
  trialDays?: number;
  /**
   * T8 — billing access ladder for the caller's own tenant. `null`/absent means the tenant is not
   * billing-managed (legacy tier-only behaviour), NOT that it is restricted. Access state only:
   * this endpoint is readable by every employee, so it carries no financial data.
   */
  accessState?: MyBillingAccessState | number | null;
  /** True when the tenant is on a self-serve billing subscription (accessState is non-null). */
  isBillingManaged?: boolean;
}

/** Mirrors `Mowazaf.Server.Data.General.Entities.Billing.BillingAccessState`. */
export type MyBillingAccessState = "Full" | "Grace" | "Restricted" | "Dormant";

const ACCESS_STATE_NAMES: MyBillingAccessState[] = ["Full", "Grace", "Restricted", "Dormant"];

/** Numbers OR strings — the API may serialize the enum either way. Unknown ⇒ null. */
export function coerceAccessState(
  value: MyBillingAccessState | number | undefined | null,
): MyBillingAccessState | null {
  if (typeof value === "number") return ACCESS_STATE_NAMES[value] ?? null;
  if (typeof value === "string" && (ACCESS_STATE_NAMES as string[]).includes(value)) {
    return value as MyBillingAccessState;
  }
  return null;
}

export interface CompanySubscriptionListResponse {
  items: CompanySubscriptionView[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/**
 * Aggregate counts for the Subscription Console KPI cards. Mirrors the backend
 * `SubscriptionStatsView`. Stable across pagination — reflects every company.
 */
export interface SubscriptionStatsView {
  totalCompanies: number;
  freeCount: number;
  trialCount: number;
  paidCount: number;
  trialAvgDaysRemaining: number;
  paidExpiringSoonCount: number;
  paidOverdueCount: number;
  trialExpiredCount: number;
  pendingUpgradeRequestsCount: number;
}

export interface SubscriptionListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  tier?: SubscriptionTier | "";
  status?: SubscriptionStatus | "";
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export interface MarkPaidInput {
  paidUntil: string | null;
  notes: string | null;
}

// CO-227 — ExtendTrialOpInput / StartTrialInput / BulkExtendTrialInput removed with the three
// operator trial routes.

export interface RevokeInput {
  notes: string;
}

/* ────────────── Enum coercion helpers (numbers OR strings) ────────────── */

const TIER_NAMES: SubscriptionTier[] = ["Free", "Trial", "Paid"];
const STATUS_NAMES: SubscriptionStatus[] = ["Active", "TrialExpired", "PaymentOverdue"];

export function coerceTier(value: SubscriptionTier | number | undefined | null): SubscriptionTier {
  if (typeof value === "number") return TIER_NAMES[value] ?? "Free";
  if (typeof value === "string" && (TIER_NAMES as string[]).includes(value)) return value as SubscriptionTier;
  return "Free";
}

export function coerceStatus(
  value: SubscriptionStatus | number | undefined | null,
): SubscriptionStatus {
  if (typeof value === "number") return STATUS_NAMES[value] ?? "Active";
  if (typeof value === "string" && (STATUS_NAMES as string[]).includes(value)) {
    return value as SubscriptionStatus;
  }
  return "Active";
}

/* ───────────────────────── Endpoints ───────────────────────── */

function toQueryString(query: SubscriptionListQuery): string {
  const params = new URLSearchParams();
  if (query.page) params.append("page", String(query.page));
  if (query.pageSize) params.append("pageSize", String(query.pageSize));
  if (query.search) params.append("search", query.search);
  if (query.tier) params.append("tier", String(query.tier));
  if (query.status) params.append("status", String(query.status));
  if (query.sortBy) params.append("sortBy", query.sortBy);
  if (query.sortDir) params.append("sortDir", query.sortDir);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Caller-scoped subscription state — read on every page-load by the banner.
 * Hits TierGuard's 30s cache on the server, so it's cheap even when polled.
 */
export async function getMySubscription() {
  const response = await request.get<Result<MySubscriptionView>>("Subscription/me");
  return response.data;
}

export async function getSubscriptionCompanies(query: SubscriptionListQuery = {}) {
  const response = await request.get<Result<CompanySubscriptionListResponse>>(
    `Subscription/companies${toQueryString(query)}`,
  );
  return response.data;
}

export async function getSubscriptionStats() {
  const response = await request.get<Result<SubscriptionStatsView>>("Subscription/stats");
  return response.data;
}

export async function getSubscriptionCompany(id: number) {
  const response = await request.get<Result<CompanySubscriptionDetailView>>(
    `Subscription/companies/${id}`,
  );
  return response.data;
}

export async function getSubscriptionHistory(id: number) {
  const response = await request.get<Result<SubscriptionAuditLogView[]>>(
    `Subscription/companies/${id}/history`,
  );
  return response.data;
}

export async function markCompanyPaid(id: number, input: MarkPaidInput) {
  const response = await request.post<Result<Company>>(
    `Subscription/companies/${id}/mark-paid`,
    input,
  );
  return response.data;
}

// CO-227 — extendCompanyTrial() / startCompanyTrial() removed; both endpoints are gone.
// mark-paid is the surviving operator grant lever.

export async function revokeCompanySubscription(id: number, input: RevokeInput) {
  const response = await request.post<Result<Company>>(
    `Subscription/companies/${id}/revoke`,
    input,
  );
  return response.data;
}

/* ───────────────────────── Phase 7 — upgrade requests ───────────────────────── */

export interface RequestUpgradeBody {
  message?: string | null;
}

export interface SubscriptionUpgradeRequestView {
  notificationId: number;
  companyId: number;
  companyName: string;
  companyImageUrl?: string | null;
  currentTier: SubscriptionTier | number;
  currentStatus: SubscriptionStatus | number;
  trialDaysRemaining?: number | null;
  userCount: number;
  requestedByUserId?: string | null;
  requestedByName?: string | null;
  message?: string | null;
  requestedAt: string;
}

/**
 * Trial/Free callers ping the Operator team. The Operator inbox + email distribution
 * receives the request. Backend rejects callers whose company is Paid.
 */
export async function requestUpgrade(body: RequestUpgradeBody) {
  const response = await request.post<Result<boolean>>("Subscription/request-upgrade", body);
  return response.data;
}

export async function getPendingUpgradeRequests() {
  const response = await request.get<Result<SubscriptionUpgradeRequestView[]>>(
    "Subscription/upgrade-requests",
  );
  return response.data;
}

export async function dismissUpgradeRequest(notificationId: number) {
  const response = await request.post<Result<boolean>>(
    `Subscription/upgrade-requests/${notificationId}/dismiss`,
    {},
  );
  return response.data;
}

/* ─────────────── Signup-reference catalog (operator-only) ─────────────── */

export async function listSignupReferences() {
  const response = await request.get<Result<SignupReferenceView[]>>("Subscription/references");
  return response.data;
}

export async function createSignupReference(input: SignupReferenceInput) {
  const response = await request.post<Result<SignupReferenceView>>(
    "Subscription/references",
    input,
  );
  return response.data;
}

export async function updateSignupReference(id: number, input: SignupReferenceInput) {
  const response = await request.put<Result<SignupReferenceView>>(
    `Subscription/references/${id}`,
    input,
  );
  return response.data;
}

export async function deleteSignupReference(id: number) {
  const response = await request.delete<Result<boolean>>(`Subscription/references/${id}`);
  return response.data;
}

/* ─────────────── Bulk operations (operator-only) ─────────────── */

/**
 * ⚠️ `notes` is REQUIRED and the server enforces it (400 otherwise), exactly like bulk revoke.
 * The dialog's own check is a nicety, not the rule — a client-side-only guard on an operator
 * override that grants paid entitlement to N companies is not a rule at all.
 */
export async function bulkMarkPaid(input: BulkMarkPaidInput) {
  const response = await request.post<Result<BulkOperationResultView>>(
    "Subscription/companies/bulk/mark-paid",
    input,
  );
  return response.data;
}

// CO-227 — bulkExtendTrial() removed; Subscription/companies/bulk/extend-trial is gone.

export async function bulkRevoke(input: BulkRevokeInput) {
  const response = await request.post<Result<BulkOperationResultView>>(
    "Subscription/companies/bulk/revoke",
    input,
  );
  return response.data;
}

/* ─────────────── HR upgrade nudge (caller in their own company) ─────────────── */

export async function notifyHrToUpgrade() {
  const response = await request.post<Result<boolean>>("Subscription/notify-hr-to-upgrade", {});
  return response.data;
}
